// Real Tabby Detection Service - Performs actual website analysis
export interface DetectionResult {
  url: string;
  hasTabby: boolean;
  confidence: number;
  detectionMethod: string;
  evidence?: {
    type: string;
    screenshot?: string;
    description: string;
    location: string;
    content?: string;
  }[];
  detectedAt: string;
  processingTime: number;
  metadata?: {
    domain: string;
    platform?: string;
    region?: string;
  };
}

export interface DetectionError {
  code: string;
  message: string;
  details?: string;
}

class RealTabbyDetectionService {
  private corsProxies = [
    'https://corsproxy.io/?',
    'https://cors-anywhere.herokuapp.com/',
    'https://api.allorigins.win/get?url=',
  ];

  // ENHANCED: Comprehensive Tabby integration indicators (optimized for deep checkout detection)
  private tabbyIndicators = {
    // CRITICAL INDICATORS (90%+ confidence) - Definitive proof of Tabby integration
    criticalScripts: [
      'checkout.tabby.ai/tabby-promo.js',
      'checkout.tabby.ai/cms-plugins.js', 
      'checkout.tabby.ai/tabby-checkout.js',
      'js.tabby.ai',
      'sdk.tabby.ai',
      // ENHANCED: Additional SDK patterns found in real implementations
      'cdn.tabby.ai',
      'widgets.tabby.ai',
      'checkout.tabby.ai/widget',
      'tabby-checkout-widget'
    ],
    
    // HIGH CONFIDENCE INDICATORS (70-85% confidence) - Strong evidence
    highConfidencePatterns: [
      /tabby[_-]?merchant[_-]?key/gi,
      /tabby[_-]?public[_-]?key/gi, 
      /tabby[_-]?api[_-]?key/gi,
      /checkout\.tabby\.ai/gi,
      /api\.tabby\.ai/gi,
      /data-tabby-/gi,
      /tabby[_-]?product[_-]?widget/gi,
      /tabby[_-]?installments?[_-]?widget/gi,
      // ENHANCED: Checkout-specific Tabby patterns (addresses centrepointstores case)
      /tabby[_-]?payment[_-]?method/gi,
      /payment[_-]?method[_-]?tabby/gi,
      /tabby[_-]?checkout[_-]?widget/gi,
      /tabby[_-]?iframe/gi,
      /tabby[_-]?button/gi,
      /class=["'][^"']*tabby[^"']*["']/gi,
      /id=["'][^"']*tabby[^"']*["']/gi,
      // ENHANCED: Dynamic payment widget patterns
      /payment[_-]?options.*tabby/gi,
      /tabby.*payment[_-]?option/gi,
      /paymentMethod.*tabby/gi,
      /tabby.*paymentMethod/gi
    ],

    // MEDIUM CONFIDENCE INDICATORS (40-60% confidence) - Enhanced for checkout detection
    mediumConfidencePatterns: [
      /buy now,?\s*pay later/gi,
      /split in 4/gi,
      /4 interest[_\s-]*free/gi,
      /tabby/gi,  // Generic tabby mention - needs context
      // ENHANCED: Checkout-specific BNPL patterns
      /pay in instalments?/gi,
      /monthly payments/gi,
      /flexible payment/gi,
      /interest[_\s-]*free instalments?/gi,
      /0%\s*interest/gi,
      /no interest/gi,
      /spread the cost/gi,
      /pay over time/gi,
      // ENHANCED: Specific payment method patterns
      /payment.*method.*tabby/gi,
      /choose.*payment.*tabby/gi,
      /select.*payment.*tabby/gi,
      /tabby.*available/gi,
      /available.*tabby/gi,
      // ENHANCED: Arabic/multilingual patterns (for MENA region)
      /قسط|تقسيط/gi,  // Arabic for "installment"
      /فوري|سريع/gi   // Arabic for "immediate/fast"
    ],

    // ENHANCED: Checkout-specific patterns (NEW CATEGORY for deep checkout detection)
    checkoutSpecificPatterns: [
      // Payment form patterns
      /name=["']payment[_-]?method["'][^>]*tabby/gi,
      /value=["'][^"']*tabby[^"']*["']/gi,
      /option[^>]*tabby/gi,
      /input[^>]*tabby/gi,
      /button[^>]*tabby/gi,
      // iFrame and widget patterns
      /iframe[^>]*tabby/gi,
      /src=["'][^"']*tabby[^"']*["']/gi,
      /widget[^>]*tabby/gi,
      // Payment method selection patterns
      /radio[^>]*tabby/gi,
      /checkbox[^>]*tabby/gi,
      /select[^>]*payment.*tabby/gi,
      // Dynamic loading patterns
      /ajax[^>]*tabby/gi,
      /fetch[^>]*tabby/gi,
      /xhr[^>]*tabby/gi
    ],

    // VERIFICATION REQUIRED - Context-dependent indicators
    contextualPatterns: [
      {
        pattern: /payment in instalments?/gi,
        requiresContext: ['tabby', 'bnpl', 'split'],
        confidence: 30
      },
      {
        pattern: /installments?/gi, 
        requiresContext: ['tabby', '4', 'interest-free'],
        confidence: 25
      },
      {
        pattern: /flexible payment/gi,
        requiresContext: ['tabby', 'split', 'later'],
        confidence: 20
      }
    ],

    // NEGATIVE INDICATORS - Reduce confidence if found
    negativeIndicators: [
      'klarna', 'afterpay', 'sezzle', 'affirm', 'zip pay', 'laybuy', 'clearpay'
    ],

    // REQUIRED DOMAINS for script verification
    officialDomains: [
      'tabby.ai',
      'checkout.tabby.ai', 
      'api.tabby.ai',
      'js.tabby.ai',
      'sdk.tabby.ai'
    ],

    // DEFINITIVE API ENDPOINTS
    apiEndpoints: [
      '/api/v2/checkout',
      '/api/v1/payments',
      'tabby.ai/checkout',
      'tabby.ai/api'
    ]
  };

  private extractDomain(url: string): string {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.toLowerCase().replace('www.', '');
    } catch {
      return '';
    }
  }

  private async fetchWithProxy(url: string, proxyIndex: number = 0, timeoutMs: number = 6000): Promise<string> {
    if (proxyIndex >= this.corsProxies.length) {
      console.warn(`🚫 All ${this.corsProxies.length} CORS proxies failed for ${url.substring(0, 50)}...`);
      throw new Error('All CORS proxies failed');
    }

    const proxy = this.corsProxies[proxyIndex];
    console.log(`🌐 Trying CORS proxy ${proxyIndex + 1}/${this.corsProxies.length}...`);
    
    const proxyStartTime = Date.now();

    try {
      // Create abort controller for timeout
      const abortController = new AbortController();
      const timeoutId = setTimeout(() => abortController.abort(), timeoutMs);

      let fetchPromise: Promise<Response>;
      let proxyUrl: string;
      
      if (proxy.includes('allorigins.win')) {
        proxyUrl = `${proxy}${encodeURIComponent(url)}`;
        fetchPromise = fetch(proxyUrl, { 
          signal: abortController.signal,
          mode: 'cors',
          credentials: 'omit',
          headers: {
            'User-Agent': 'Mozilla/5.0 (compatible; TabbyChecker/1.0)',
            'Accept': 'application/json, text/plain, */*'
          }
        });
      } else {
        proxyUrl = `${proxy}${url}`;
        fetchPromise = fetch(proxyUrl, { 
          signal: abortController.signal,
          mode: 'cors',
          credentials: 'omit',
          headers: {
            'User-Agent': 'Mozilla/5.0 (compatible; TabbyChecker/1.0)',
            'Accept': 'text/html, application/xhtml+xml, */*'
          }
        });
      }

      const response = await fetchPromise;
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }
      
      const proxyTime = Date.now() - proxyStartTime;
      
      if (proxy.includes('allorigins.win')) {
        const data = await response.json();
        console.log(`✅ AllOrigins proxy succeeded in ${proxyTime}ms`);
        return data.contents || '';
      } else {
        console.log(`✅ Generic proxy succeeded in ${proxyTime}ms`);
        return await response.text();
      }
    } catch (error: any) {
      const proxyTime = Date.now() - proxyStartTime;
      
      // Suppress detailed CORS errors in console - just log minimal info
      if (error.name === 'AbortError') {
        console.warn(`⏰ Proxy timed out after ${timeoutMs}ms, trying next...`);
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.warn(`🚫 Network error, trying next proxy...`);
      } else {
        console.warn(`❌ Proxy failed (${error.message}), trying next...`);
      }
      
      // Try next proxy
      return this.fetchWithProxy(url, proxyIndex + 1, timeoutMs);
    }
  }

  // ENHANCED: Discover checkout flows and simulate deep navigation
  private discoverRelevantPages(html: string, baseUrl: string): string[] {
    console.log(`🔍 ENHANCED: Discovering checkout flows and payment pages...`);
    const pages: string[] = [];
    const htmlLower = html.toLowerCase();
    const urlObj = new URL(baseUrl);
    const baseOrigin = urlObj.origin;
    
    // PRIORITY 1: Deep checkout flow detection (ENHANCED for cases like centrepointstores.com)
    const deepCheckoutPatterns = [
      // Standard checkout patterns
      /href=["']([^"']*checkout[^"']*)["']/gi,
      /href=["']([^"']*cart[^"']*)["']/gi,
      /href=["']([^"']*payment[^"']*)["']/gi,
      /href=["']([^"']*pay[^"']*)["']/gi,
      /href=["']([^"']*billing[^"']*)["']/gi,
      /href=["']([^"']*order[^"']*)["']/gi,
      // ENHANCED: Multi-step checkout patterns commonly used by major retailers
      /href=["']([^"']*step[^"']*)["']/gi,
      /href=["']([^"']*shipping[^"']*)["']/gi,
      /href=["']([^"']*delivery[^"']*)["']/gi,
      /href=["']([^"']*quick[^"']*checkout[^"']*)["']/gi,
      /href=["']([^"']*guest[^"']*checkout[^"']*)["']/gi,
      /href=["']([^"']*secure[^"']*checkout[^"']*)["']/gi,
      // ENHANCED: Platform-specific checkout URLs
      /href=["']([^"']*\/checkout\/[^"']*)["']/gi,
      /href=["']([^"']*\/cart\/[^"']*)["']/gi,
      /href=["']([^"']*\/payment\/[^"']*)["']/gi,
    ];

    // PRIORITY 2: Product pages (second most likely)
    const productPagePatterns = [
      /href=["']([^"']*product[^"']*)["']/gi,
      /href=["']([^"']*item[^"']*)["']/gi,
      /href=["']([^"']*jewelry[^"']*)["']/gi,
      /href=["']([^"']*jewellery[^"']*)["']/gi,
      /href=["']([^"']*watch[^"']*)["']/gi,
      /href=["']([^"']*ring[^"']*)["']/gi,
      /href=["']([^"']*necklace[^"']*)["']/gi,
      /href=["']([^"']*buy[^"']*)["']/gi
    ];

    // PRIORITY 3: Shop and category pages  
    const shopPagePatterns = [
      /href=["']([^"']*shop[^"']*)["']/gi,
      /href=["']([^"']*collection[^"']*)["']/gi,
      /href=["']([^"']*category[^"']*)["']/gi,
      /href=["']([^"']*catalog[^"']*)["']/gi,
      /href=["']([^"']*store[^"']*)["']/gi
    ];

    // STEP 1: ENHANCED Checkout Flow Discovery (PRIORITY: Centrepointstores-style deep checkout)
    console.log(`🎯 ENHANCED: Discovering deep checkout flows and payment steps...`);
    for (const pattern of deepCheckoutPatterns) {
      const matches = html.matchAll(pattern);
      for (const match of matches) {
        let url = match[1];
        if (url.startsWith('/')) {
          url = baseOrigin + url;
        } else if (!url.startsWith('http')) {
          continue;
        }
        if (url.startsWith(baseOrigin) && !pages.includes(url) && !url.includes('#') && !url.includes('javascript:')) {
          pages.push(url);
          console.log(`🎯 CHECKOUT FLOW: ${url}`);
          if (pages.length >= 8) break; // More checkout pages for deeper analysis
        }
      }
    }

    // STEP 1.5: ENHANCED - Generate common checkout paths even if not found in HTML
    console.log(`➕ ENHANCED: Adding probable checkout paths...`);
    const probableCheckoutPaths = [
      '/checkout', '/checkout/', '/checkout/payment', '/checkout/step/payment',
      '/cart', '/cart/', '/cart/checkout', '/shopping-cart', '/shopping-cart/',
      '/payment', '/payment/', '/payments', '/payments/',
      '/order', '/order/', '/order/payment', '/order/checkout',
      '/quick-checkout', '/guest-checkout', '/secure-checkout',
      '/billing', '/billing/', '/billing/payment', '/delivery/payment',
      // Platform-specific paths
      '/index.php/checkout', '/en/checkout', '/checkout.php',
      // Multi-step patterns
      '/checkout/step/1', '/checkout/step/2', '/checkout/step/3',
      '/checkout/shipping', '/checkout/payment', '/checkout/review'
    ];
    
    for (const path of probableCheckoutPaths) {
      const url = baseOrigin + path;
      if (!pages.includes(url)) {
        pages.push(url);
        console.log(`➕ PROBABLE CHECKOUT: ${url}`);
        if (pages.length >= 15) break; // Expanded limit for comprehensive checkout detection
      }
    }

    // STEP 2: Extract product pages
    console.log(`🛍️  Searching for product pages...`);
    for (const pattern of productPagePatterns) {
      const matches = html.matchAll(pattern);
      for (const match of matches) {
        let url = match[1];
        if (url.startsWith('/')) {
          url = baseOrigin + url;
        } else if (!url.startsWith('http')) {
          continue;
        }
        if (url.startsWith(baseOrigin) && !pages.includes(url) && !url.includes('#') && !url.includes('javascript:')) {
          pages.push(url);
          console.log(`🛍️  PRODUCT PAGE: ${url}`);
          if (pages.length >= 8) break; // More product pages
        }
      }
    }

    // STEP 3: Extract shop/category pages
    console.log(`🏪 Searching for shop and category pages...`);
    for (const pattern of shopPagePatterns) {
      const matches = html.matchAll(pattern);
      for (const match of matches) {
        let url = match[1];
        if (url.startsWith('/')) {
          url = baseOrigin + url;
        } else if (!url.startsWith('http')) {
          continue;
        }
        if (url.startsWith(baseOrigin) && !pages.includes(url) && !url.includes('#') && !url.includes('javascript:')) {
          pages.push(url);
          console.log(`🏪 SHOP PAGE: ${url}`);
          if (pages.length >= 10) break; // Total limit
        }
      }
    }

    // STEP 4: Add common e-commerce paths if not found
    console.log(`➕ Adding common e-commerce paths...`);
    const commonPaths = ['/checkout', '/cart', '/shop', '/products', '/store', '/catalog'];
    for (const path of commonPaths) {
      const url = baseOrigin + path;
      if (!pages.includes(url)) {
        pages.push(url);
        console.log(`➕ COMMON PATH: ${url}`);
        if (pages.length >= 12) break;
      }
    }

    console.log(`📋 Discovered ${pages.length} pages for comprehensive deep analysis`);
    return pages.slice(0, 12); // Increased to 12 pages for thorough analysis
  }

  private analyzeHTMLContent(html: string, pageUrl: string = 'homepage'): {
    hasTabby: boolean;
    confidence: number;
    evidence: DetectionResult['evidence'];
    detectionMethod: string;
    pageType: string;
  } {
    console.log(`🔬 Starting ADVANCED verification on ${html.length} characters from ${pageUrl}...`);
    const analysisStartTime = Date.now();
    
    const evidence: DetectionResult['evidence'] = [];
    let confidenceScore = 0;
    const htmlLower = html.toLowerCase();
    const pageType = this.detectPageType(html, pageUrl);
    let verificationLevel = 'none';

    // 1. CRITICAL INDICATORS - Definitive proof (90%+ confidence)
    console.log(`🚨 Checking critical Tabby JavaScript integrations...`);
    let criticalMatches = 0;
    for (const script of this.tabbyIndicators.criticalScripts) {
      if (htmlLower.includes(script.toLowerCase())) {
        criticalMatches++;
        const scoreAdd = 90;
        verificationLevel = 'definitive';
        console.log(`🎯 CRITICAL: Found definitive Tabby script: ${script} (+${scoreAdd} confidence)`);
        evidence.push({
          type: 'critical_integration',
          description: `DEFINITIVE: Core Tabby SDK detected: ${script}`,
          location: `${pageType} - JavaScript integration`,
          content: script
        });
        confidenceScore += scoreAdd;
      }
    }

    // 2. HIGH CONFIDENCE PATTERNS - Strong evidence (70-85% confidence)
    console.log(`🔍 Checking high-confidence integration patterns...`);
    let highConfidenceMatches = 0;
    for (const pattern of this.tabbyIndicators.highConfidencePatterns) {
      const matches = html.match(pattern);
      if (matches) {
        highConfidenceMatches++;
        const scoreAdd = 75;
        if (verificationLevel === 'none') verificationLevel = 'high';
        console.log(`✅ HIGH CONFIDENCE: Found integration pattern: ${matches[0]} (+${scoreAdd} confidence)`);
        evidence.push({
          type: 'high_confidence_integration',
          description: `Strong technical evidence: ${pattern.toString()}`,
          location: `${pageType} - Implementation details`,
          content: matches[0].substring(0, 100) + (matches[0].length > 100 ? '...' : '')
        });
        confidenceScore += scoreAdd;
        break; // One high-confidence match is sufficient
      }
    }

    // 3. OFFICIAL DOMAIN VERIFICATION (High importance)
    console.log(`🔍 Verifying official Tabby domains...`);
    let officialDomainFound = false;
    for (const domain of this.tabbyIndicators.officialDomains) {
      if (htmlLower.includes(domain)) {
        officialDomainFound = true;
        console.log(`✅ OFFICIAL: Found official Tabby domain: ${domain} (+40 confidence)`);
        confidenceScore += 40;
        evidence.push({
          type: 'official_domain',
          description: `Official Tabby domain reference: ${domain}`,
          location: `${pageType} - External resources`,
          content: domain
        });
        if (verificationLevel === 'none') verificationLevel = 'medium';
      }
    }

    // 4. MEDIUM CONFIDENCE PATTERNS - Requires context verification
    console.log(`🔍 Checking medium-confidence patterns with context verification...`);
    let validMediumMatches = 0;
    for (const pattern of this.tabbyIndicators.mediumConfidencePatterns) {
      const matches = html.match(pattern);
      if (matches) {
        let scoreAdd = 0;
        
        // Special handling for generic "tabby" mentions
        if (pattern.toString().includes('tabby/gi')) {
          const context = this.extractContext(html, matches[0], 150);
          if (this.isTabbyInPaymentContext(context)) {
            scoreAdd = 30;
            validMediumMatches++;
            console.log(`✅ VERIFIED: Tabby mention in payment context (+${scoreAdd} confidence)`);
          } else {
            scoreAdd = 5;
            console.log(`⚠️  WEAK: Generic Tabby mention without payment context (+${scoreAdd} confidence)`);
          }
        } else {
          scoreAdd = 35;
          validMediumMatches++;
          console.log(`✅ MEDIUM: Found pattern: ${matches[0]} (+${scoreAdd} confidence)`);
        }
        
        confidenceScore += scoreAdd;
        evidence.push({
          type: scoreAdd > 20 ? 'verified_pattern' : 'weak_indicator',
          description: `${scoreAdd > 20 ? 'Verified' : 'Weak'} indicator: ${pattern.toString()}`,
          location: `${pageType} - Content analysis`,
          content: matches[0].substring(0, 100)
        });
      }
    }

    // 5. ENHANCED: CHECKOUT-SPECIFIC PATTERN DETECTION (NEW)
    console.log(`🛒 ENHANCED: Checking checkout-specific Tabby patterns...`);
    let checkoutSpecificMatches = 0;
    if (this.tabbyIndicators.checkoutSpecificPatterns) {
      for (const pattern of this.tabbyIndicators.checkoutSpecificPatterns) {
        const matches = html.match(pattern);
        if (matches) {
          checkoutSpecificMatches++;
          const scoreAdd = 60; // High confidence for checkout-specific patterns
          console.log(`✅ CHECKOUT-SPECIFIC: Found checkout Tabby integration: ${matches[0].substring(0, 50)}... (+${scoreAdd} confidence)`);
          confidenceScore += scoreAdd;
          evidence.push({
            type: 'checkout_specific_integration',
            description: `Checkout-specific Tabby integration: ${pattern.toString()}`,
            location: `${pageType} - Payment form analysis`,
            content: matches[0].substring(0, 150)
          });
          if (verificationLevel === 'none') verificationLevel = 'checkout-verified';
          break; // One checkout-specific match is highly significant
        }
      }
    }

    // 6. CONTEXTUAL VERIFICATION
    console.log(`🔍 Checking contextual patterns...`);
    for (const contextItem of this.tabbyIndicators.contextualPatterns) {
      const matches = html.match(contextItem.pattern);
      if (matches) {
        const hasRequiredContext = contextItem.requiresContext.some(keyword => 
          htmlLower.includes(keyword.toLowerCase())
        );
        
        if (hasRequiredContext) {
          console.log(`✅ CONTEXTUAL: Verified pattern with required context (+${contextItem.confidence} confidence)`);
          confidenceScore += contextItem.confidence;
          evidence.push({
            type: 'contextual_verified',
            description: `Contextually verified: ${contextItem.pattern.toString()}`,
            location: `${pageType} - Contextual analysis`,
            content: matches[0].substring(0, 100)
          });
        }
      }
    }

    // 6. NEGATIVE INDICATORS - Competing services reduce confidence
    console.log(`🚫 Checking for competing BNPL services...`);
    let negativeScore = 0;
    for (const competitor of this.tabbyIndicators.negativeIndicators) {
      if (htmlLower.includes(competitor.toLowerCase())) {
        negativeScore += 20;
        console.log(`❌ NEGATIVE: Found competitor: ${competitor} (-20 confidence)`);
        evidence.push({
          type: 'negative_indicator',
          description: `Competing BNPL service: ${competitor}`,
          location: `${pageType} - Competition analysis`,
          content: competitor
        });
      }
    }
    confidenceScore = Math.max(0, confidenceScore - negativeScore);

    // 7. STRICT CONFIDENCE CALCULATION
    let finalConfidence = confidenceScore;
    
    // Apply page type bonuses only for verified detections
    if (confidenceScore > 50 && (pageType.includes('product') || pageType.includes('checkout'))) {
      finalConfidence += 5;
      console.log(`📈 Page context bonus: +5 for ${pageType}`);
    }
    
    finalConfidence = Math.min(finalConfidence, 95);
    
    // ENHANCED VERIFICATION: Improved logic for checkout-specific detection
    const hasDefinitiveEvidence = criticalMatches > 0 || highConfidenceMatches > 0 || officialDomainFound;
    const hasCheckoutEvidence = checkoutSpecificMatches > 0; // NEW: Checkout-specific detection
    const hasStrongEvidence = finalConfidence >= 60 && (hasDefinitiveEvidence || hasCheckoutEvidence);
    const hasModerateEvidence = finalConfidence >= 40 && validMediumMatches >= 2;
    // ENHANCED: Checkout-specific evidence counts as strong evidence (addresses centrepointstores case)
    const hasTabby = hasStrongEvidence || hasModerateEvidence || hasCheckoutEvidence;
    
    const analysisTime = Date.now() - analysisStartTime;
    console.log(`🧮 ENHANCED Verification complete in ${analysisTime}ms:`);
    console.log(`   📊 Score: ${confidenceScore} → Final: ${finalConfidence}%`);
    console.log(`   🎯 Verification: ${verificationLevel}`);
    console.log(`   🔍 Definitive Evidence: ${hasDefinitiveEvidence ? 'YES' : 'NO'}`);
    console.log(`   🛒 Checkout Evidence: ${hasCheckoutEvidence ? 'YES' : 'NO'}`); // NEW
    console.log(`   ✅ Result: ${hasTabby ? 'TABBY VERIFIED' : 'INSUFFICIENT EVIDENCE'}`);
    console.log(`   📋 Evidence: ${evidence.length} items (${criticalMatches} critical, ${highConfidenceMatches} high-conf, ${checkoutSpecificMatches} checkout, ${validMediumMatches} medium)`);

    const detectionMethod = hasTabby 
      ? `Enhanced deep checkout verification (${verificationLevel} level) - ${evidence.length} indicators on ${pageType}${hasCheckoutEvidence ? ' [CHECKOUT DETECTION]' : ''}`
      : `Enhanced verification found insufficient evidence across ${pageType} - requires definitive technical proof or checkout-specific integration for positive detection`;

    return {
      hasTabby,
      confidence: finalConfidence,
      evidence,
      detectionMethod,
      pageType
    };
  }

  // Helper method to extract context around a match
  private extractContext(html: string, match: string, contextLength: number = 150): string {
    const index = html.toLowerCase().indexOf(match.toLowerCase());
    if (index === -1) return match;
    
    const start = Math.max(0, index - contextLength);
    const end = Math.min(html.length, index + match.length + contextLength);
    
    return html.substring(start, end);
  }

  // Helper method to determine if "tabby" mention is in payment context
  private isTabbyInPaymentContext(context: string): boolean {
    const contextLower = context.toLowerCase();
    const paymentKeywords = [
      'payment', 'checkout', 'pay', 'installment', 'bnpl', 'later', 
      'split', 'monthly', 'interest-free', 'method', 'option', 'cart',
      'buy now', 'purchase', 'billing', 'finance'
    ];
    
    const keywordCount = paymentKeywords.filter(keyword => 
      contextLower.includes(keyword)
    ).length;
    
    return keywordCount >= 2; // Require at least 2 payment-related keywords
  }

  // ENHANCED: Better page type detection with checkout priority
  private detectPageType(html: string, url: string): string {
    const htmlLower = html.toLowerCase();
    const urlLower = url.toLowerCase();
    
    // ENHANCED: More comprehensive checkout detection
    if (this.isLikelyCheckoutPage(url) || htmlLower.includes('payment method') || 
        htmlLower.includes('billing information') || htmlLower.includes('order summary')) {
      return 'checkout page';
    }
    
    if (urlLower.includes('product') || urlLower.includes('item') || 
        htmlLower.includes('add to cart') || htmlLower.includes('buy now') ||
        htmlLower.includes('add to bag') || htmlLower.includes('price')) {
      return 'product page';
    }
    
    if (urlLower.includes('shop') || urlLower.includes('collection') || urlLower.includes('category')) {
      return 'shop page';
    }
    
    return 'homepage';
  }

  // ENHANCED: Helper method to identify checkout pages
  private isLikelyCheckoutPage(url: string): boolean {
    const urlLower = url.toLowerCase();
    const checkoutKeywords = [
      'checkout', 'cart', 'payment', 'pay', 'billing', 'order',
      'step', 'shipping', 'delivery', 'secure-checkout', 'quick-checkout'
    ];
    
    return checkoutKeywords.some(keyword => urlLower.includes(keyword));
  }

  private detectPlatform(html: string): string | undefined {
    const htmlLower = html.toLowerCase();
    console.log(`🏪 Detecting e-commerce platform...`);
    
    if (htmlLower.includes('shopify') || htmlLower.includes('cdn.shopify')) {
      console.log(`✅ Platform detected: Shopify`);
      return 'Shopify';
    }
    if (htmlLower.includes('woocommerce') || htmlLower.includes('wc-')) {
      console.log(`✅ Platform detected: WooCommerce`);
      return 'WooCommerce';
    }
    if (htmlLower.includes('magento')) {
      console.log(`✅ Platform detected: Magento`);
      return 'Magento';
    }
    if (htmlLower.includes('bigcommerce')) {
      console.log(`✅ Platform detected: BigCommerce`);
      return 'BigCommerce';
    }
    if (htmlLower.includes('wordpress')) {
      console.log(`✅ Platform detected: WordPress`);
      return 'WordPress';
    }
    if (htmlLower.includes('opencart')) {
      console.log(`✅ Platform detected: OpenCart`);
      return 'OpenCart';
    }
    if (htmlLower.includes('prestashop')) {
      console.log(`✅ Platform detected: PrestaShop`);
      return 'PrestaShop';
    }
    
    console.log(`❓ Platform: Unknown/Custom`);
    return undefined;
  }

  private getRegion(domain: string): string {
    if (domain.endsWith('.ae') || domain.endsWith('.sa') || domain.endsWith('.kw') || domain.endsWith('.qa')) {
      return 'Middle East';
    }
    if (domain.endsWith('.eg') || domain.endsWith('.ma') || domain.endsWith('.dz')) {
      return 'North Africa';  
    }
    if (domain.endsWith('.com') || domain.endsWith('.org') || domain.endsWith('.net')) {
      return 'Global';
    }
    return 'Unknown';
  }

  async checkTabbySupport(url: string): Promise<DetectionResult> {
    const startTime = Date.now();
    console.log(`\n🚀 === ENHANCED TABBY DETECTION STARTED ===`);
    console.log(`📍 Target URL: ${url}`);
    
    try {
      // Validate URL
      console.log(`⚙️  Step 1/6: Validating URL format...`);
      new URL(url);
      const domain = this.extractDomain(url);
      
      if (!domain) {
        throw new Error('Invalid domain extracted from URL');
      }

      console.log(`✅ URL validation passed`);
      console.log(`🌐 Domain: ${domain}`);
      console.log(`📊 Region: ${this.getRegion(domain)}`);

      // Fetch homepage content
      console.log(`⚙️  Step 2/6: Fetching homepage content...`);
      const fetchStartTime = Date.now();
      const html = await this.fetchWithProxy(url, 0, 6000); // 6 second timeout for homepage
      const fetchTime = Date.now() - fetchStartTime;
      
      if (!html || html.length < 100) {
        throw new Error('Unable to fetch website content or content too short');
      }

      console.log(`✅ Homepage content fetch completed in ${fetchTime}ms`);
      console.log(`📄 Retrieved ${html.length} characters from ${domain}`);

      // Detect platform
      console.log(`⚙️  Step 3/6: Analyzing platform...`);
      const platform = this.detectPlatform(html);
      
      // Discover additional pages to check
      console.log(`⚙️  Step 4/6: Discovering additional pages...`);
      const additionalPages = this.discoverRelevantPages(html, url);
      
      // Analyze homepage first
      console.log(`⚙️  Step 5/6: Multi-page Tabby integration scanning...`);
      console.log(`🔎 Analyzing homepage...`);
      const homepageAnalysis = this.analyzeHTMLContent(html, 'homepage');
      
      let bestAnalysis = homepageAnalysis;
      let allEvidence = [...(homepageAnalysis.evidence || [])];
      let pagesChecked = ['homepage'];
      
      // COMPREHENSIVE ANALYSIS: Always check additional pages for thorough detection
      // Tabby integration is often only visible on product/checkout pages, not homepage
      if (additionalPages.length > 0) {
        console.log(`🕵️  Starting comprehensive multi-page analysis...`);
        console.log(`📄 Homepage result: ${homepageAnalysis.confidence}% confidence, hasTabby: ${homepageAnalysis.hasTabby}`);
        console.log(`🔍 Will analyze ${additionalPages.length} additional pages for deeper detection...`);
        
        let successfulPageChecks = 0;
        const maxPagesToCheck = Math.min(additionalPages.length, 8); // ENHANCED: More pages for thorough checkout detection
        let checkoutPagesFound = 0;
        
        for (let i = 0; i < maxPagesToCheck; i++) {
          // Check if we're approaching our time limit (extended for comprehensive analysis)
          const elapsed = Date.now() - startTime;
          if (elapsed > 18000) { // Extended to 18 seconds for enhanced checkout analysis
            console.log(`⏰ Approaching 18s limit (${elapsed}ms), stopping additional checks`);
            break;
          }
          
          const pageUrl = additionalPages[i];
          const isCheckoutPage = this.isLikelyCheckoutPage(pageUrl);
          
          try {
            console.log(`🔎 ENHANCED Deep Analysis (${i + 1}/${maxPagesToCheck}): ${pageUrl} ${isCheckoutPage ? '[CHECKOUT PRIORITY]' : ''}`);
            // Use longer timeout for checkout pages (6 seconds) as they're more important
            const timeout = isCheckoutPage ? 6000 : 4000;
            const pageHtml = await this.fetchWithProxy(pageUrl, 0, timeout);
            const pageAnalysis = this.analyzeHTMLContent(pageHtml, pageUrl);
            
            pagesChecked.push(pageAnalysis.pageType);
            successfulPageChecks++;
            if (isCheckoutPage) checkoutPagesFound++;
            
            // Add evidence from this page
            if (pageAnalysis.evidence) {
              allEvidence.push(...pageAnalysis.evidence);
            }
            
            // ENHANCED: Prioritize checkout page results and checkout-specific detections
            const isCheckoutDetection = pageAnalysis.evidence?.some(e => e.type === 'checkout_specific_integration');
            const shouldUpdateBest = pageAnalysis.confidence > bestAnalysis.confidence || 
                                   (isCheckoutDetection && !bestAnalysis.hasTabby) ||
                                   (pageAnalysis.hasTabby && !bestAnalysis.hasTabby);
                                   
            if (shouldUpdateBest) {
              bestAnalysis = pageAnalysis;
              console.log(`🎆 ENHANCED: Better result found on ${pageAnalysis.pageType}: ${pageAnalysis.confidence}% ${isCheckoutDetection ? '[CHECKOUT VERIFIED]' : ''}`);
            }
            
            // ENHANCED: If we find strong checkout evidence, still continue to find all checkout pages
            if (isCheckoutDetection) {
              console.log(`✅ CHECKOUT DETECTION CONFIRMED! Continuing to find additional checkout integrations...`);
            }
            
            console.log(`📊 Page analysis complete. Continuing comprehensive scan...`);
          } catch (error: any) {
            console.warn(`⚠️  Failed to analyze page ${pageUrl} (skipping):`, error?.message || 'Network error');
            // Continue with next page instead of stopping
            continue;
          }
        }
        
        console.log(`📊 ENHANCED analysis complete: ${successfulPageChecks}/${maxPagesToCheck} pages analyzed (${checkoutPagesFound} checkout pages)`);
        console.log(`📈 Total pages checked: ${pagesChecked.length} (including homepage)`);
      } else {
        console.log(`ℹ️  No additional pages discovered for analysis - relying on homepage results only`);
      }
      
      const analysis = bestAnalysis;
      
      // Final result compilation
      console.log(`⚙️  Step 6/6: Compiling comprehensive results...`);
      const processingTime = Date.now() - startTime;
      
      // Calculate confidence based on thoroughness of analysis, not evidence found
      let analysisConfidence = 60; // Base confidence for successful basic analysis
      
      // Add confidence based on number of pages successfully analyzed
      const pagesAnalyzed = pagesChecked.length;
      if (pagesAnalyzed >= 2) analysisConfidence += 15; // Multi-page analysis
      if (pagesAnalyzed >= 4) analysisConfidence += 10; // Comprehensive analysis
      if (pagesAnalyzed >= 7) analysisConfidence += 5;  // Very thorough analysis
      
      // Add confidence based on page variety (different types analyzed)
      const uniquePageTypes = [...new Set(pagesChecked)];
      if (uniquePageTypes.length >= 2) analysisConfidence += 5; // Different page types
      if (uniquePageTypes.includes('checkout page')) analysisConfidence += 5; // Critical checkout page found
      
      // Subtract confidence for analysis issues
      if (processingTime > 12000) analysisConfidence -= 5; // Analysis took too long
      if (pagesAnalyzed < 2) analysisConfidence -= 10; // Limited analysis scope
      
      // Ensure confidence stays within reasonable bounds
      const finalConfidence = Math.min(Math.max(analysisConfidence, 65), 95);
      
      // Determine if Tabby was found based on evidence strength, not confidence
      const totalEvidence = allEvidence.length;
      const hasStrongEvidence = allEvidence.some(e => 
        e.type === 'critical_integration' || 
        e.type === 'high_confidence_integration' ||
        e.type === 'official_domain'
      );
      const hasModerateEvidence = totalEvidence >= 2 && allEvidence.some(e => e.type === 'verified_pattern');
      const hasTabby = hasStrongEvidence || hasModerateEvidence;

      console.log(`\n🎯 === ENHANCED DETECTION SUMMARY ===`);
      console.log(`🌐 Website: ${domain}`);
      console.log(`🏪 Platform: ${platform || 'Unknown'}`);
      console.log(`📄 Pages Checked: ${pagesChecked.join(', ')}`);
      console.log(`🔍 Tabby Status: ${hasTabby ? '✅ FOUND' : '❌ NOT FOUND'}`);
      console.log(`📊 Final Confidence: ${finalConfidence}%`);
      console.log(`📋 Total Evidence: ${totalEvidence} indicators across ${pagesChecked.length} pages`);
      console.log(`⏱️  Total Time: ${processingTime}ms`);
      console.log(`🚀 === ENHANCED DETECTION COMPLETE ===\n`);

      return {
        url,
        hasTabby,
        confidence: finalConfidence,
        detectionMethod: `Multi-page analysis across ${pagesChecked.length} pages: ${pagesChecked.join(', ')}`,
        evidence: allEvidence,
        detectedAt: new Date().toISOString(),
        processingTime,
        metadata: {
          domain,
          platform,
          region: this.getRegion(domain)
        }
      };

    } catch (error) {
      const processingTime = Date.now() - startTime;
      console.log(`\n💥 === DETECTION FAILED ===`);
      console.log(`📍 URL: ${url}`);
      console.log(`❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      console.log(`⏱️  Failed after: ${processingTime}ms`);
      console.log(`💥 === END ERROR REPORT ===\n`);
      
      throw {
        code: 'DETECTION_FAILED',
        message: 'Failed to analyze website',
        details: error instanceof Error ? error.message : 'Unknown error occurred'
      } as DetectionError;
    }
  }

  async isValidURL(url: string): Promise<boolean> {
    try {
      const urlObj = new URL(url);
      return !!(urlObj.hostname && urlObj.protocol.startsWith('http'));
    } catch {
      return false;
    }
  }

  // ENHANCED: Get updated detection statistics
  getDetectionStats() {
    return {
      detectionMethod: 'ENHANCED Deep Checkout Flow Analysis - Multi-page real-time detection',
      indicatorsChecked: [
        'Tabby JavaScript SDK & CDN patterns',
        'Payment method form elements',
        'Checkout-specific integrations', // NEW
        'Dynamic payment widgets', // NEW  
        'CSS classes, IDs & data attributes',
        'Official domain references',
        'Contextual payment patterns',
        'BNPL competitor analysis',
        'Multi-lingual pattern matching' // NEW
      ],
      pagesChecked: [
        'Homepage (baseline analysis)',
        'Deep checkout flow pages (PRIORITY)', // ENHANCED
        'Multi-step payment pages (NEW)', // NEW
        'Product pages (smart discovery)',
        'Shop/collection pages (comprehensive)',
        'Platform-specific checkout paths (NEW)' // NEW
      ],
      enhancements: [ // NEW SECTION
        'Checkout-specific pattern detection',
        'Extended checkout path discovery', 
        'Payment form analysis',
        'Dynamic widget detection',
        'Multi-step checkout support'
      ],
      targetCompletionTime: 18000, // ms - extended for comprehensive checkout analysis
      averageDetectionTime: 8000, // ms - longer due to deeper analysis
      maxPagesAnalyzed: 8, // ENHANCED from 6
      checkoutFocus: true, // NEW
      lastUpdated: new Date().toISOString()
    };
  }
}

// Export singleton instance
export const realTabbyDetectionService = new RealTabbyDetectionService();
export default realTabbyDetectionService;


